import { lazy, Suspense } from "react";
import ErrorBoundary from "../../Components/ErrorBoundary";
import Loader from "../../Components/Loader";
import { validateComponentType } from "../../Security/ConfigValidator";
import { SecurityMonitor } from "../../Security/XSSProtection";

// Lazy load components for better performance and security
const SimpleElement = lazy(() => import("./SimpleElement"));
const UpperLabeledTabsElement = lazy(() => import("./UpperLabeledTabsElement"));

// Secure component registry with validation
const COMPONENT_REGISTRY = Object.freeze({
    Simple: {
        component: SimpleElement,
        requiredProps: ['object'],
        allowedProps: ['object', 'type', 'className', 'style'],
        description: 'Simple content element'
    },
    UpperLabeledTabs: {
        component: UpperLabeledTabsElement,
        requiredProps: ['object'],
        allowedProps: ['object', 'type', 'className', 'style'],
        description: 'Tabbed interface element'
    },
});

/**
 * Validates component props against allowed props
 */
const validateComponentProps = (componentType, props) => {
    const registry = COMPONENT_REGISTRY[componentType];
    
    if (!registry) {
        throw new Error(`Unknown component type: ${componentType}`);
    }

    // Check required props
    for (const requiredProp of registry.requiredProps) {
        if (!(requiredProp in props)) {
            throw new Error(`Missing required prop '${requiredProp}' for component ${componentType}`);
        }
    }

    // Check for unauthorized props
    const propKeys = Object.keys(props);
    const unauthorizedProps = propKeys.filter(prop => !registry.allowedProps.includes(prop));
    
    if (unauthorizedProps.length > 0) {
        SecurityMonitor.reportViolation('UNAUTHORIZED_PROPS', `Component ${componentType} received unauthorized props`, {
            componentType,
            unauthorizedProps,
            allProps: propKeys
        });
        
        // Filter out unauthorized props
        const filteredProps = {};
        for (const prop of registry.allowedProps) {
            if (prop in props) {
                filteredProps[prop] = props[prop];
            }
        }
        return filteredProps;
    }

    return props;
};

/**
 * Secure wrapper component for dashboard elements with performance monitoring
 */
const SecureElementWrapper = ({ componentType, props, children }) => {

    return (
        <ErrorBoundary
            fallback={
                <div style={{ 
                    padding: '20px', 
                    border: '1px solid #ff0000', 
                    borderRadius: '4px',
                    backgroundColor: '#fff5f5',
                    color: '#d32f2f'
                }}>
                    <h4>Component Error</h4>
                    <p>Failed to render {componentType} component safely.</p>
                </div>
            }
        >
            <Suspense fallback={<Loader />}>
                {children}
            </Suspense>
        </ErrorBoundary>
    );
};

/**
 * Secure dashboard element renderer
 */
const getDashboardElement = (spec) => {
    try {
        // Validate spec structure
        if (!spec || typeof spec !== 'object') {
            throw new Error('Invalid spec: must be an object');
        }

        if (!spec.type) {
            throw new Error('Invalid spec: type is required');
        }

        // Validate component type
        const componentType = validateComponentType(spec.type);
        
        // Get component from registry
        const registry = COMPONENT_REGISTRY[componentType];
        if (!registry) {
            throw new Error(`Component type '${componentType}' is not registered`);
        }

        // Validate and sanitize props
        const validatedProps = validateComponentProps(componentType, spec);
        const sanitizedProps = SecurityMonitor.validateComponentProps(componentType, validatedProps);

        // Create the component
        const Component = registry.component;
        
        return (
            <SecureElementWrapper componentType={componentType} props={sanitizedProps}>
                <Component {...sanitizedProps} />
            </SecureElementWrapper>
        );

    } catch (error) {
        SecurityMonitor.reportViolation('COMPONENT_RENDER_ERROR', error.message, {
            spec,
            error: error.message
        });

        console.error('Failed to render dashboard element:', error);

        // Return safe fallback component
        return (
            <div style={{ 
                padding: '20px', 
                border: '1px solid #ff9800', 
                borderRadius: '4px',
                backgroundColor: '#fff8e1',
                color: '#e65100'
            }}>
                <h4>Render Error</h4>
                <p>Component could not be rendered safely: {error.message}</p>
            </div>
        );
    }
};

/**
 * Gets list of available component types
 */
export const getAvailableComponentTypes = () => {
    return Object.keys(COMPONENT_REGISTRY);
};

/**
 * Gets component registry information
 */
export const getComponentInfo = (componentType) => {
    return COMPONENT_REGISTRY[componentType] || null;
};

export default getDashboardElement;